import { useState, useCallback, useEffect, useMemo } from 'react';

/**
 * Custom hook for managing mention autocomplete state
 *
 * Handles @ trigger detection, member filtering, keyboard navigation,
 * and selection for implementing @mention autocomplete in message input.
 *
 * @param {Array<{userId: string, name?: string, rawDisplayName?: string}>} roomMembers - Room member list
 * @param {string|null} currentUserId - Current user's Matrix ID to exclude from suggestions (prevents self-mentions)
 * @param {(userId: string) => boolean} [needsDisambiguation] - Returns true when a userId's display name is
 *   globally ambiguous (from DisambiguationContext). Used to embed the MXID in the inserted mention text so it
 *   resolves unambiguously. Defaults to a no-op so the hook stays usable without the Disambiguation provider.
 * @returns {Object} Autocomplete state and handlers
 */
const useMentionAutocomplete = (roomMembers = [], currentUserId = null, needsDisambiguation = () => false) => {
  // Autocomplete visibility
  const [isOpen, setIsOpen] = useState(false);

  // Current search text after @
  const [searchText, setSearchText] = useState('');

  // Currently highlighted suggestion index
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Trigger position in the input text
  const [triggerPosition, setTriggerPosition] = useState(null);

  /**
   * Maximum number of suggestions to show
   */
  const MAX_SUGGESTIONS = 7;

  /**
   * Filter room members based on search text
   * Matches against displayName and Matrix ID localpart (case-insensitive)
   * Excludes current user to prevent self-mentions
   * Only computes when autocomplete is open
   */
  const suggestions = useMemo(() => {
    if (!isOpen || !roomMembers?.length) return [];

    const search = searchText.toLowerCase();

    const filtered = roomMembers
      .filter(member => {
        // Exclude current user from suggestions (prevent self-mentions)
        if (currentUserId && member.userId === currentUserId) {
          return false;
        }

        const displayName = member.rawDisplayName || member.name || '';
        const userId = member.userId || '';

        // Extract localpart from Matrix ID (@alice:server.com -> alice)
        const localpart = userId.startsWith('@')
          ? userId.slice(1).split(':')[0]
          : '';

        // Case-insensitive match on displayName or localpart
        return (
          displayName.toLowerCase().includes(search) ||
          localpart.toLowerCase().includes(search)
        );
      })
      .sort((a, b) => {
        const nameA = (a.rawDisplayName || a.name || a.userId).toLowerCase();
        const nameB = (b.rawDisplayName || b.name || b.userId).toLowerCase();
        return nameA.localeCompare(nameB);
      })
      .slice(0, MAX_SUGGESTIONS);

    return filtered;
  }, [isOpen, roomMembers, searchText, currentUserId]);

  /**
   * Reset selectedIndex when suggestions change
   */
  useEffect(() => {
    setSelectedIndex(0);
  }, [suggestions]);

  /**
   * Detect @ trigger and update autocomplete state
   *
   * @param {string} value - Current input value
   * @param {number} cursorPosition - Current cursor position
   */
  const handleInputChange = useCallback((value, cursorPosition) => {
    // Get text from start to cursor position
    const textBeforeCursor = value.slice(0, cursorPosition);

    // Detect @ trigger at word boundary
    // Match @ at start or after whitespace, followed by optional word chars, dots, or hyphens
    // Supports usernames like @terry.tucker or @john-doe
    const triggerMatch = textBeforeCursor.match(/(?:^|\s)@([\w.-]*)$/);

    if (triggerMatch) {
      const matchStart = textBeforeCursor.lastIndexOf('@');
      setIsOpen(true);
      setSearchText(triggerMatch[1] || '');
      setTriggerPosition({
        start: matchStart,
        end: cursorPosition,
      });
    } else {
      // No @ trigger found - close autocomplete
      if (isOpen) {
        setIsOpen(false);
        setSearchText('');
        setTriggerPosition(null);
      }
    }
  }, [isOpen]);

  /**
   * Generate formatted mention text for a member.
   *
   * Produces one of three forms, designed to round-trip through
   * extractMentionsFromBody/resolveMentionsToUserIds in utils/mentionProcessing.js:
   *   - `@[Name (@user:server)]` — when the display name is ambiguous, either
   *     globally (needsDisambiguation) or within the room (hasRoomDuplicate).
   *     Resolves via the resolver's embedded-MXID branch.
   *   - `@[Name]` — when the display name has any non-word character
   *     (space, dot, hyphen, non-ASCII, …). Resolves via the bracketed group,
   *     provided the bracketed name matches a member's display name or localpart.
   *   - `@Name` — bare form, only for pure /^\w+$/ display names.
   *
   * Edge: a member with NO display name falls back to its userId, yielding a
   * bracketed raw MXID like `@[@noname:server]`. That form does not resolve back
   * (it is neither a display name nor a localpart), so such a mention renders as
   * plain text — an accepted, pre-existing limitation for nameless members.
   *
   * @param {Object} member - Room member object
   * @returns {string} Formatted mention text (one of the three forms above)
   */
  const formatMentionText = useCallback((member) => {
    const displayName = member.rawDisplayName || member.name || member.userId;
    const normalizedName = displayName.toLowerCase();

    // Embed the MXID when the display name is ambiguous, so the mention resolves
    // to a single user via the embedded-MXID branch in resolveMentionsToUserIds:
    //   - globally (needsDisambiguation): aligns with the dropdown row, which
    //     already shows the MXID for globally-duplicate names.
    //   - within this room (hasRoomDuplicate): safety net for same-name members
    //     not yet reflected in the global names list (e.g. a just-invited
    //     duplicate before the next sync tick refreshes useContactNames).
    const hasRoomDuplicate = roomMembers.some(m =>
      m.userId !== member.userId &&
      (m.rawDisplayName || m.name || '').toLowerCase() === normalizedName,
    );
    if (needsDisambiguation(member.userId) || hasRoomDuplicate) {
      // e.g. @[Duplicate Name (@dupe1:server.com)]
      return `@[${displayName} (${member.userId})]`;
    }

    // A bare @name only round-trips through the extractor for pure word chars.
    // Anything else (space, dot, hyphen, colon, brackets, non-ASCII, …) must use
    // the bracketed form, which the extractor captures via its [^\]]+ group.
    // (The old /[\s:()[\]<>]/ test missed '.' and '-', so SSO display names like
    // "annamaria.schuermann" took the bare path and failed to resolve — NED-4096.)
    if (!/^\w+$/.test(displayName)) {
      return `@[${displayName}]`;
    }

    return `@${displayName}`;
  }, [roomMembers, needsDisambiguation]);

  /**
   * Handle mention selection
   *
   * @param {Object} member - Selected room member
   * @returns {Object} { mentionText, startIndex, endIndex } for text replacement
   */
  const selectMention = useCallback((member) => {
    if (!member || !triggerPosition) return null;

    const mentionText = formatMentionText(member);

    const result = {
      mentionText,
      startIndex: triggerPosition.start,
      endIndex: triggerPosition.end,
    };

    // Close autocomplete after selection
    setIsOpen(false);
    setSearchText('');
    setSelectedIndex(0);
    setTriggerPosition(null);

    return result;
  }, [triggerPosition, formatMentionText]);

  /**
   * Handle keyboard navigation and selection
   *
   * @param {KeyboardEvent} event - Keyboard event
   * @returns {boolean} True if event was consumed
   */
  const handleKeyDown = useCallback((event) => {
    if (!isOpen || !suggestions.length) return false;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev =>
          prev >= suggestions.length - 1 ? 0 : prev + 1,
        );
        return true;

      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev =>
          prev <= 0 ? suggestions.length - 1 : prev - 1,
        );
        return true;

      case 'Enter':
        if (suggestions[selectedIndex]) {
          event.preventDefault();
          // Return the selected mention for the caller to handle
          return selectMention(suggestions[selectedIndex]);
        }
        return false;

      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        setSearchText('');
        setSelectedIndex(0);
        setTriggerPosition(null);
        return true;

      case 'Tab':
        // Tab can also select current suggestion
        if (suggestions[selectedIndex]) {
          event.preventDefault();
          return selectMention(suggestions[selectedIndex]);
        }
        return false;

      default:
        return false;
    }
  }, [isOpen, suggestions, selectedIndex, selectMention]);

  /**
   * Close the autocomplete and reset state
   */
  const close = useCallback(() => {
    setIsOpen(false);
    setSearchText('');
    setSelectedIndex(0);
    setTriggerPosition(null);
  }, []);

  return {
    isOpen,
    suggestions,
    selectedIndex,
    searchText,
    triggerPosition,
    handleInputChange,
    handleKeyDown,
    selectMention,
    close,
  };
};

export default useMentionAutocomplete;
